/**
 * 10.9IH — TOUR GUIADO PARA INICIANTES. Thiago: "quero criar um tutorial e explicar como funciona
 * cada parte do jogo pros iniciantes, eles escolhem se pula ou se segue o tutorial".
 *
 * O que já existia era o tutorial CONTEXTUAL (`core/Tutorial.ts`): uma dica solta, em banner, no
 * instante em que a mecânica acontece. Isso é ótimo pra lembrar, mas não ensina — o jogador novo
 * cai no mundo sem saber que o ataque é automático, que a bolsa briga sozinha, que existe Árvore.
 *
 * Este é o outro lado: uma sequência CURTA que aponta o dedo pra cada parte da tela, uma por vez,
 * e explica. Regras de projeto:
 *  - É OPCIONAL e a escolha vem primeiro: o primeiro cartão pergunta "seguir ou pular".
 *  - NÃO pausa o jogo e NÃO bloqueia o toque: o buraco do holofote deixa o controle real visível e
 *    clicável, então dá pra ANDAR no passo que ensina a andar. Só o cartão captura o toque.
 *  - Passos que dependem de algo que o jogador ainda não tem (botão de magia, poção) se escondem
 *    sozinhos via `when`.
 *  - Sai a qualquer momento pelo "Pular", e dá pra refazer depois pelo "Como jogar".
 *
 * O que já foi visto vive em `progress.tips` (a mesma lista do tutorial contextual), pra não
 * inventar campo novo no save nem subir a versão dele à toa.
 */
import { i18n } from "../i18n/i18n";

export interface TourStep {
  /** Estável: entra no save quando o tour termina. */
  id: string;
  /** Chave i18n do título. */
  title: string;
  /** Chave i18n do corpo. */
  text: string;
  /** Elemento a destacar. Seletor CSS ou função (o elemento pode nascer depois). */
  target?: string | (() => Element | null | undefined);
  /** Passo só entra na sequência se isto for verdade (ex.: só mago tem botão de magia). */
  when?: () => boolean;
  /** Avança sozinho quando vira verdade (ex.: o jogador andou). Sem isto, avança no toque. */
  until?: () => boolean;
  /** Roda ao entrar no passo. */
  onEnter?: () => void;
}

export interface GuideTourHandlers {
  /** Terminou (chegou ao fim) ou pulou. `finished` diferencia os dois. */
  onEnd: (finished: boolean) => void;
}

/** De quanto em quanto tempo o holofote é recolocado e o `until` é conferido (Hz). */
const TICK_MS = 60;
/** Folga em volta do elemento destacado. */
const PAD = 8;
/** Abaixo desta altura o cartão encolhido fica ilegível — não vale a pena encolher mais. */
const ALTURA_MINIMA = 110;
/** Distância mínima entre o cartão e a borda da tela. */
const MARGEM = 8;

export class GuideTour {
  private readonly root: HTMLDivElement;
  private readonly hole: HTMLDivElement;
  private readonly card: HTMLDivElement;
  private readonly titleEl: HTMLElement;
  private readonly textEl: HTMLElement;
  private readonly stepEl: HTMLElement;
  private readonly nextBtn: HTMLButtonElement;
  private readonly skipBtn: HTMLButtonElement;

  private steps: TourStep[] = [];
  private i = -1;
  private timer = 0;
  private running = false;

  constructor(uiRoot: HTMLElement, private readonly h: GuideTourHandlers) {
    this.root = document.createElement("div");
    this.root.className = "tour";
    this.root.hidden = true;
    // pointer-events fica no CSS: a camada é transparente ao toque, só o cartão captura.
    this.root.innerHTML = `
      <div class="tour-hole" hidden></div>
      <div class="tour-card">
        <div class="tour-step"></div>
        <h3 class="tour-title"></h3>
        <p class="tour-text"></p>
        <div class="tour-btns">
          <button class="btn tour-skip" data-a="skip"></button>
          <button class="btn primary tour-next" data-a="next"></button>
        </div>
      </div>`;
    uiRoot.appendChild(this.root);
    this.hole = this.root.querySelector(".tour-hole")!;
    this.card = this.root.querySelector(".tour-card")!;
    this.titleEl = this.root.querySelector(".tour-title")!;
    this.textEl = this.root.querySelector(".tour-text")!;
    this.stepEl = this.root.querySelector(".tour-step")!;
    this.nextBtn = this.root.querySelector('[data-a="next"]')!;
    this.skipBtn = this.root.querySelector('[data-a="skip"]')!;
    this.nextBtn.addEventListener("click", () => this.advance());
    this.skipBtn.addEventListener("click", () => this.end(false));
  }

  get active(): boolean {
    return this.running;
  }

  /** Começa o tour. Passos com `when` falso já saem fora aqui. */
  start(steps: TourStep[]): void {
    this.steps = steps.filter((s) => !s.when || s.when());
    if (this.steps.length === 0) {
      this.h.onEnd(true);
      return;
    }
    this.i = -1;
    this.running = true;
    this.root.hidden = false;
    this.advance();
    window.clearInterval(this.timer);
    this.timer = window.setInterval(() => this.tick(), TICK_MS);
  }

  /** Interrompe sem avisar (troca de personagem, voltar ao menu…). */
  stop(): void {
    if (!this.running) return;
    this.running = false;
    window.clearInterval(this.timer);
    this.root.hidden = true;
  }

  private end(finished: boolean): void {
    this.stop();
    this.h.onEnd(finished);
  }

  private advance(): void {
    this.i++;
    if (this.i >= this.steps.length) {
      this.end(true);
      return;
    }
    const s = this.steps[this.i];
    s.onEnter?.();
    this.stepEl.textContent = `${this.i + 1}/${this.steps.length}`;
    this.titleEl.textContent = i18n.t(s.title);
    this.textEl.textContent = i18n.t(s.text);
    this.skipBtn.textContent = i18n.t("guide.skip");
    // Passo que espera uma AÇÃO não mostra "Próximo": o botão viraria um atalho pra não fazer.
    const waits = !!s.until;
    this.nextBtn.hidden = waits;
    // No PRIMEIRO cartao o botao nao diz "Proximo": diz "Seguir o tutorial". Ali o jogador nao
    // esta avancando um passo, esta ESCOLHENDO entre fazer e pular — o rotulo tem que dizer isso.
    const rotulo = this.i === 0 ? "guide.start" : this.i === this.steps.length - 1 ? "guide.finish" : "guide.next";
    this.nextBtn.textContent = i18n.t(rotulo);
    this.card.classList.toggle("waiting", waits);
    this.place();
  }

  private tick(): void {
    if (!this.running) return;
    const s = this.steps[this.i];
    if (!s) return;
    if (s.until && s.until()) {
      this.advance();
      return;
    }
    this.place();
  }

  /** Recoloca o holofote e o cartão. Chamado a cada tick: o alvo se mexe (barra de vida flutua). */
  private place(): void {
    const s = this.steps[this.i];
    const el = this.resolve(s?.target);
    const r = el?.getBoundingClientRect();
    // Alvo inexistente ou invisível (largura zero) = passo vira cartão centralizado, sem buraco.
    if (!r || r.width < 1 || r.height < 1) {
      this.hole.hidden = true;
      // Sem buraco não há sombra de 9999px, então o escurecido passa pra própria camada:
      // sem isso o cartão flutuaria sobre o jogo em brilho normal e perderia o foco.
      this.root.classList.add("no-hole");
      this.card.classList.add("centered");
      this.soltaLateral();
      this.card.style.removeProperty("top");
      this.card.style.removeProperty("bottom");
      return;
    }
    this.hole.hidden = false;
    this.root.classList.remove("no-hole");
    this.hole.style.left = `${r.left - PAD}px`;
    this.hole.style.top = `${r.top - PAD}px`;
    this.hole.style.width = `${r.width + PAD * 2}px`;
    this.hole.style.height = `${r.height + PAD * 2}px`;
    // O cartão vai pro vão (acima ou abaixo do alvo) onde ele CABE INTEIRO — não basta olhar qual
    // vão é maior: a barra de atalhos tem 300 px de altura na borda direita e deixava 167 px de
    // sobra pra um cartão de 188, então o cartão nascia meio metro pra fora da tela.
    // Se não cabe em nenhum dos dois, fica no extremo de menor sobreposição, sempre dentro da tela.
    this.card.classList.remove("centered");
    this.card.style.bottom = "auto";
    this.card.style.removeProperty("max-height"); // mede a altura NATURAL antes de decidir
    const alt = this.card.offsetHeight;
    const teto = window.innerHeight - alt - MARGEM;
    const vaoAcima = r.top - PAD * 2 - MARGEM;
    const vaoAbaixo = window.innerHeight - r.bottom - PAD * 2 - MARGEM;
    if (vaoAbaixo >= alt || vaoAcima >= alt) {
      this.soltaLateral();
      const topo = vaoAbaixo >= alt ? r.bottom + PAD * 2 : r.top - PAD * 2 - alt;
      this.card.style.top = `${Math.round(Math.max(MARGEM, Math.min(teto, topo)))}px`;
      return;
    }
    // Não cabe nem acima nem abaixo. É o caso da coluna de atalhos: 320 px de altura encostada na
    // borda direita. Mas sobra tela do LADO dela, então o cartão encolhe e vai pro lado oposto, em
    // vez de ficar por cima justamente do que está sendo apontado.
    const esq = r.left - MARGEM * 2;
    const dir = window.innerWidth - r.right - MARGEM * 2;
    const lado = Math.max(esq, dir);
    if (lado >= 200) {
      this.card.classList.add("side");
      this.card.style.width = `${Math.round(lado)}px`;
      this.card.style.left = dir >= esq ? `${Math.round(r.right + MARGEM * 2)}px` : `${MARGEM}px`;
      const altLado = this.card.offsetHeight; // mudou a largura: a altura mudou junto
      const tetoLado = window.innerHeight - altLado - MARGEM;
      this.card.style.top = `${Math.round(Math.max(MARGEM, Math.min(tetoLado, r.top)))}px`;
      return;
    }
    // Alvo LARGO (a barra de armas ocupa a tela inteira) numa tela baixa: não dá pra ir pro lado.
    // Aí o cartão ENCOLHE pro vão maior e rola por dentro, em vez de cobrir o que está apontando.
    this.soltaLateral();
    const vao = Math.max(vaoAcima, vaoAbaixo);
    if (vao >= ALTURA_MINIMA) {
      this.card.style.maxHeight = `${Math.round(vao)}px`;
      const topo = vaoAbaixo >= vaoAcima ? r.bottom + PAD * 2 : Math.max(MARGEM, r.top - PAD * 2 - vao);
      this.card.style.top = `${Math.round(topo)}px`;
      return;
    }
    // Nem encolhendo cabe: abaixo disso o cartão fica ilegível. Vai pro extremo de menor estrago.
    this.card.style.top = `${Math.round(vaoAbaixo >= vaoAcima ? Math.max(MARGEM, teto) : MARGEM)}px`;
  }

  /** Desfaz o modo lateral (volta a ser um cartão largo e centralizado na horizontal). */
  private soltaLateral(): void {
    this.card.classList.remove("side");
    this.card.style.removeProperty("width");
    this.card.style.removeProperty("left");
  }

  private resolve(t: TourStep["target"]): Element | null {
    if (!t) return null;
    if (typeof t === "string") return document.querySelector(t);
    return t() ?? null;
  }
}
